import { motion } from 'framer-motion';
import { LucideIcon, Check } from 'lucide-react';
import { useRotatingImage } from '../../hooks/useRotatingImage';

interface Feature {
  icon?: LucideIcon;
  text: string;
}

interface ScreenshotShowcaseProps {
  imageSrc?: string;
  imageSrcs?: string[];
  alt?: string;
  direction?: 'left' | 'right';
  title: string;
  subtitle?: string;
  features: Feature[];
  accentColor?: string;
  placeholderIcon?: LucideIcon;
}

export default function ScreenshotShowcase({
  imageSrc,
  imageSrcs,
  alt = '',
  direction = 'left',
  title,
  subtitle,
  features,
  accentColor = '#00BCD4',
  placeholderIcon: PlaceholderIcon,
}: ScreenshotShowcaseProps) {
  // Use rotating images if array provided, otherwise fall back to single image
  const pool = imageSrcs && imageSrcs.length > 0 ? imageSrcs : imageSrc ? [imageSrc] : [];
  const { currentSrc, isFading } = useRotatingImage(pool, 150000);

  const imageContent = (
    <motion.div
      initial={{ opacity: 0, x: direction === 'left' ? -30 : 30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative group"
    >
      <div className="rounded-2xl overflow-hidden shadow-2xl shadow-gray-200/50 ring-1 ring-black/5">
        {currentSrc ? (
          <img
            src={currentSrc}
            alt={alt}
            className="w-full h-auto max-h-[380px] object-cover group-hover:scale-[1.02] transition-all duration-700"
            style={{ opacity: isFading ? 0 : 1, transition: 'opacity 0.6s ease-in-out', objectPosition: '50% 30%' }}
            loading="lazy"
          />
        ) : (
          <div
            className="w-full aspect-[16/10] flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${accentColor}08, ${accentColor}18)` }}
          >
            {PlaceholderIcon && (
              <PlaceholderIcon className="w-16 h-16 opacity-20" style={{ color: accentColor }} />
            )}
          </div>
        )}
      </div>
      {/* Colored accent shadow */}
      <div
        className="absolute -z-10 top-4 inset-x-4 bottom-0 rounded-2xl blur-2xl opacity-15"
        style={{ backgroundColor: accentColor }}
      />
    </motion.div>
  );

  const textContent = (
    <motion.div
      initial={{ opacity: 0, x: direction === 'left' ? 30 : -30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col justify-center"
    >
      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 tracking-tight">{title}</h3>
      {subtitle && <p className="text-gray-500 mb-6 text-base leading-relaxed">{subtitle}</p>}
      <ul className="space-y-3">
        {features.map((feature, i) => (
          <li key={i} className="flex items-start gap-3">
            <div
              className="w-6 h-6 rounded-lg flex-shrink-0 flex items-center justify-center mt-0.5"
              style={{ backgroundColor: `${accentColor}12` }}
            >
              <Check className="w-3.5 h-3.5" style={{ color: accentColor }} />
            </div>
            <span className="text-gray-600 text-[15px] leading-relaxed">{feature.text}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      {direction === 'left' ? (
        <>
          {imageContent}
          {textContent}
        </>
      ) : (
        <>
          {textContent}
          {imageContent}
        </>
      )}
    </div>
  );
}
